/* eslint-disable jsx-a11y/anchor-is-valid */
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { IconName, IconPrefix } from '@fortawesome/pro-solid-svg-icons';
import { Button, Dropdown, Menu, message } from 'antd';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { queryCache } from 'react-query';
import { TableEntry } from '~components/atoms/FileTables/FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { useModalActions } from '~src/contexts/ModalsContext';
import dataContextMenu from '~src/data/dataContextMenu';
import { useToggleFavouriteFolder } from '~src/hooks/actions/useToggleFavouriteFolder';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  addToFavoriteFiles,
  editFile,
  removeFromFavoriteFiles,
} from '~src/services/api';
import { updateCachedFile } from '~src/services/cache/files';
import { RIGHTS_LEVEL } from '~src/types/enums';
import { downloadFileLastVersion } from '~src/utils/download';
import { appLog } from '~src/utils/log';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { ReactComponent as AutodeskRightClickIcon } from '~src/assets/svf-icons/autodeskRightClick.svg';

interface DotMenuProps {
  item: TableEntry;
  menuItems: typeof dataContextMenu.file;
  permissionLevel: RIGHTS_LEVEL | 0;
}

const DotMenu: React.FC<DotMenuProps> = ({
  item,
  menuItems,
  permissionLevel,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const isSuperuser = useIsSuperuser();
  const projectId = useProjectId();

  const { toggleFavouriteFolder } = useToggleFavouriteFolder();
  const editFileModal = useModalActions('editFileModal');
  const editFolderModal = useModalActions('editFolderModal');
  const deleteModal = useModalActions('deleteModal');

  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { setFileToView } = useFileViewerContext();

  const toggleFavourite = async () => {
    if (item.dbFile) {
      const fileId = item.dbFile.id;
      const nextIsFavourite = !item.dbFile.is_favorite;
      try {
        if (nextIsFavourite) {
          await addToFavoriteFiles({
            data: {
              ...item.dbFile,
              is_favorite: nextIsFavourite,
            },
          });
        } else {
          await removeFromFavoriteFiles({
            data: {
              ...item.dbFile,
              is_favorite: nextIsFavourite,
            },
          });
        }

        // to void dropdown stutter
        setTimeout(() => {
          // show immediate UI update
          updateCachedFile(
            fileId,
            { is_favorite: nextIsFavourite },
            { projectId: String(projectId), withVersions: true }
          );
          queryCache.invalidateQueries('files');
        }, 200);
      } catch (err) {
        nextIsFavourite
          ? appLog('DotMenu -> addToFavoriteFiles -> err', err)
          : appLog('DotMenu -> removeFromFavoriteFiles -> err', err);
        message.error(intl.formatMessage({ id: 'favourite-failed' }));
      }
    } else if (item.folder && item.dbFolder) {
      toggleFavouriteFolder(item.dbFolder);
    }
  };

  const onItemClick = (e) => {
    appLog('e => ', e);

    // Direct Autodesk open on mobile dot menu (no submenu)
    if (e?.name === 'svf-view-file' && item?.dbFile) {
      // pick latest version: context first, then first version from the row
      const v =
        fileVersionsContext[item.dbFile.id] ??
        (item.dbFile.versions && item.dbFile.versions[0]);

      const state = v?.extension_svf_model?.state ?? 0;
      const isViewable = state === 7;

      if (isViewable) {
        setFileToView({ ...item.dbFile });
      } else {
        message.info(intl.formatMessage({ id: 'svf-view-file-progress' }));
      }
      return;
    }

    switch (e.modal) {
      case 'folder-edit':
        editFolderModal.open({ folder: item.dbFolder });
        break;
      case 'delete':
        deleteModal.open({
          files: item.dbFile ? [item.dbFile] : [],
          folders: item.dbFolder ? [item.dbFolder] : undefined,
        });
        break;
      case 'favourite':
        toggleFavourite();
        break;
      case 'file-edit':
        editFileModal.open({ file: item.dbFile });
        break;
      case 'file-download':
        if (item.dbFile)
          downloadFileLastVersion(item.dbFile, fileVersionsContext).catch(
            (err) => {
              appLog('downloadFile -> err', err);
              message.error(intl.formatMessage({ id: 'file-download-failed' }));
            }
          );
        break;
    }
  };

  const overlay = (
    <Menu>
      {menuItems.map((menuItem) => {
        // hide su options for normal users
        // TO BE ADDED BACK WHEN IMPLEMENTED
        if (menuItem.needsSU && !isSuperuser) {
          return null;
        }

        let disabled;
        // special case of 'rights-managment' and 'restore-to', should be enabled for SU
        if (
          (item.name === 'rights-managment' || item.name === 'restore-to') &&
          isSuperuser
        ) {
          disabled = false;
        } else {
          disabled = permissionLevel < menuItem.minPermissionLevel;
        }

        // === Mobile-specific rules for Autodesk menu item ===
        if (menuItem.name === 'svf-view-file') {
          // show ONLY for CAD files
          const ext = (item.extension || '').toLowerCase();
          const isCad = ['dwg', 'rvt', 'ifc', 'nwd'].includes(ext);
          if (!isCad || !item?.dbFile) return null;

          // enable only when model is viewable (state === 7)
          const v =
            fileVersionsContext[item.dbFile.id] ??
            (item.dbFile.versions && item.dbFile.versions[0]);
          const state = v?.extension_svf_model?.state ?? 0;
          const isViewable = state === 7;
          disabled = disabled || !isViewable;

          return (
            <Menu.Item
              key={menuItem.key}
              disabled={disabled}
              style={{
                flex: 1,
                paddingLeft: '23px',
              }}
              title={
                disabled
                  ? intl.formatMessage({ id: 'permission-restricted' })
                  : undefined
              }
              onClick={() => onItemClick(menuItem)}>
              {/* Use Autodesk stateful icon instead of generic icon */}
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}>
                <AutodeskRightClickIcon
                  css={styles.customSvgIcon}
                  style={disabled ? { color: 'grey' } : undefined}
                />
                <span css={styles.text}>
                  {intl.formatMessage({ id: menuItem.name })}
                </span>
              </div>
            </Menu.Item>
          );
        }

        // === Default rendering for all other menu items ===
        return (
          <Menu.Item
            key={menuItem.key}
            css={styles.item}
            disabled={disabled}
            title={
              disabled
                ? intl.formatMessage({ id: 'permission-restricted' })
                : undefined
            }
            onClick={() => onItemClick(menuItem)}>
            <IconButton
              icon={[
                menuItem.iconStyle as IconPrefix,
                menuItem.icon as IconName,
              ]}
              customStyle={styles.icon}
              backgroundOff
            />

            <span css={styles.text}>
              {intl.formatMessage({ id: menuItem.name })}
            </span>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  return (
    <Dropdown
      css={{ position: 'absolute', right: 0 }}
      overlay={overlay}
      trigger={['click']}
      placement="bottomLeft">
      <a
        className="ant-dropdown-link"
        onClick={(e) => e.preventDefault()}
        css={styles.dotAnchor}>
        <IconButton
          iconStyle="fal"
          icon="ellipsis-v"
          customStyle={styles.dotIcon}
          color={'gray'}
          backgroundOff
        />
      </a>
    </Dropdown>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  menu: {
    padding: '5px 0px',
  },
  dotIcon: {
    marginRight: 0,
    fontSize: '30px',
  },
  customSvgIcon: {
    width: '17px',
    height: '17px',
    color: theme.colors.secondary,
    marginRight: '5px',
  },
  dotAnchor: {
    color: 'gray',
    textDecoration: 'none',
    '&:hover': {
      color: 'gray',
    },
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    padding: '2px 15px',
    cursor: 'pointer',

    '&:active': {
      backgroundColor: theme.colors.primaryLight,
    },
    '&:hover': {
      backgroundColor:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? `${theme.colors.secondaryLight} !important`
          : '',
    },

    '&:not(.ant-dropdown-menu-item-disabled)': {
      color: theme.colors.text.primary,
    },
  },
  icon: {
    color: theme.colors.secondary,
    fontSize: '16px',
  },
  text: {
    fontSize: '16px',
    padding: '3px 6px',
    userSelect: 'none',
  },
});
export default DotMenu;
